/**
 * Image optimization utilities for Next.js Image component
 */

export function generateBlurDataURL(imageId: string | null | undefined): string {
  // Return a minimal blur placeholder if no image
  if (!imageId) {
    return 'data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAABAAEDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAUEAEAAAAAAAAAAAAAAAAAAAAA/8QAFQEBAQAAAAAAAAAAAAAAAAAAAAX/xAAUEQEAAAAAAAAAAAAAAAAAAAAA/9oADAMBAAIRAxEAPwCdABmX/9k='
  }
  
  // For Directus images, create a low-quality version for blur placeholder
  const baseUrl = process.env.NEXT_PUBLIC_DIRECTUS_URL?.replace('/api', '') || ''
  
  // Accepts a bare asset id, a Directus /assets/<id> URL, or a canonical
  // /img/<id>/<slug>.jpg path. The blur source always comes straight from
  // Directus: it is a 10px throwaway, not a surface worth proxying.
  if (typeof imageId === 'string') {
    const canonical = imageId.match(/\/img\/([0-9a-fA-F-]{20,})\//)
    if (canonical) {
      imageId = canonical[1]
    } else if (imageId.startsWith('http')) {
      const matches = imageId.match(/\/assets\/([^?]+)/)
      if (matches) {
        imageId = matches[1]
      }
    }
  }

  // Return a very low quality, small version for blur effect
  return `${baseUrl}/assets/${imageId}?width=10&height=10&quality=10&format=webp`
}

export function getOptimizedImageSizes(context: 'hero' | 'card' | 'thumbnail' | 'content' | 'featured'): string {
  const sizeMap = {
    hero: "(max-width: 768px) 100vw, 896px",
    card: "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 400px", 
    thumbnail: "(max-width: 768px) 25vw, (max-width: 1200px) 20vw, 150px",
    content: "(max-width: 768px) 100vw, (max-width: 1200px) 70vw, 800px",
    featured: "(max-width: 768px) 100vw, (max-width: 1200px) 90vw, 1200px"
  }
  return sizeMap[context]
}

export function getImageDimensions(context: 'hero' | 'card' | 'thumbnail' | 'content' | 'featured'): { width: number; height: number } {
  const dimensionMap = {
    hero: { width: 1200, height: 600 },
    card: { width: 400, height: 225 },
    thumbnail: { width: 150, height: 150 },
    content: { width: 800, height: 450 },
    featured: { width: 1200, height: 630 }
  }
  return dimensionMap[context]
}

/** Longest edge of the canonical representation served by /img/<id>/<slug>.jpg. */
export const CANONICAL_IMAGE_WIDTH = 2048

/**
 * True for a local `/public/examples` asset: the species reference photos, weed
 * and pest photos, and the diagnose sample shots.
 *
 * These have to render with `unoptimized` so next/image leaves the bare
 * `/examples/...` path in `src`. Next's optimizer would rewrite it to
 * `/_next/image?url=...`, which public/robots.txt disallows, while
 * sitemap-image.xml and every ImageObject.contentUrl cite the bare path. The
 * page would then show Google a URL it is told not to fetch, which is the same
 * canonical-URL mismatch phase 1 of the image-search work removed for Directus
 * media. The files are pre-cropped to their display size, so the optimizer has
 * nothing to do for them anyway.
 *
 * Directus media is NOT covered here: it goes through directusImageLoader,
 * which already puts the canonical /img/ path in src.
 */
export function isIndexableLocalAsset(src: unknown): boolean {
  return typeof src === 'string' && src.startsWith('/examples/')
}

/**
 * next/image loader for canonical same-origin image URLs.
 *
 * Given a bare `/img/<assetId>/<slug>.jpg` (see getCanonicalImageUrl), emit the
 * responsive variant `/img/<assetId>/<slug>.jpg?w=<width>`. The variant carries
 * X-Robots-Tag: noindex, so the breakpoint URLs never compete with the bare
 * canonical path for indexing while the browser still gets a right-sized image.
 *
 * Anything that is not a canonical path -- data URIs, placeholders, third-party
 * image_url values -- is returned untouched. A custom loader replaces Next's
 * optimizer entirely, so passing those through is what keeps them rendering.
 */
export function directusImageLoader({ src, width, quality }: { src: string; width: number; quality?: number }) {
  if (src.startsWith('data:') || src.includes('placeholder')) {
    return src
  }

  const [path, existingQuery] = src.split('?')
  if (!/^(?:https?:\/\/[^/]+)?\/img\/[0-9a-fA-F-]{20,}\/[a-z0-9-]{1,80}\.jpg$/.test(path)) {
    return src
  }

  // At or above the canonical width the bare path IS the right image, and
  // returning it puts the canonical, indexable URL in the src attribute that
  // next/image builds from the largest srcset candidate. Keep this in step with
  // CANONICAL_WIDTH in app/img/[assetId]/[filename]/route.ts.
  if (width >= CANONICAL_IMAGE_WIDTH) {
    return path
  }

  const params = new URLSearchParams(existingQuery || '')
  params.set('w', String(width))
  if (quality) params.set('q', String(quality))

  return `${path}?${params.toString()}`
}
